<template>
  <div class="stk-p-8">
    <p class="title is-4">
      Designate and Group Model Portfolios
    </p>

    <!-- Model Sets Section -->
    <div class="box">
      <p class="block">Create groups of model portfolios available for best fit matching (e.g. tactical series, etf models...)</p>
      <button class="button is-primary" @click="showCreateModelSetInput = true">
        Create New Grouping of Model Portfolios
      </button>

      <div v-if="showCreateModelSetInput" class="mt-3">
        <input v-model="newModelSetName" class="input" type="text" placeholder="Enter Model Set Name">
        <button class="button is-success mt-2" @click="saveNewModelSet">
          Save
        </button>
        <button class="button is-light mt-2" @click="cancelCreateModelSet">
          Cancel
        </button>
      </div>
    </div>

    <!-- Strategy Tags Section -->
    <div class="box">
      <p class="block">Create strategy tags to categorize model portfolios (e.g. conservative, growth, ESG...)</p>
      <button class="button is-primary" @click="showCreateStrategyInput = true">
        Create New Strategy Tag
      </button>

      <div v-if="showCreateStrategyInput" class="mt-3">
        <input v-model="newStrategyName" class="input" type="text" placeholder="Enter Strategy Tag Name">
        <button class="button is-success mt-2" @click="saveNewStrategy">
          Save
        </button>
        <button class="button is-light mt-2" @click="cancelCreateStrategy">
          Cancel
        </button>
      </div>
    </div>

    <!-- Model Sets Table -->
    <div v-if="modelSets.length > 0">
      <p class="title is-5">
        Model Portfolio Groups
      </p>

      <b-table
        :data="modelSets"
        ref="table"
        detailed
        detail-key="id"
        :opened-detailed.sync="openedDetails"
        :show-detail-icon="true"
        detail-transition="fade"
      >
        <b-table-column field="id" :visible="false">
        </b-table-column>
        <b-table-column
          field="name"
          label="Name"
          sortable
        >
          <template slot-scope="props">
            <span class="icon is-small is-left" @click="removeModelSet(props.row.id)" style="cursor: pointer; margin-right: 5px;">
              <i class="fas fa-trash"></i>
            </span>

            <!-- Optional custom caret you can click anywhere on the cell -->
            <button class="button is-white is-small caret-btn"
                    @click.stop="toggleRowDetails(props.row)">
              <span class="icon">
                <i class="fas fa-chevron-right"
                  :class="{ 'rotated': openedDetails.includes(props.row.id) }"></i>
              </span>
            </button>

            <strong>{{ props.row.name }}</strong>
            <span class="tag is-light is-rounded"
                  style="margin-left:.5rem;">
              {{ (props.row.model_portfolios || []).length }} portfolios
            </span>
          </template>
        </b-table-column>

        <!-- (Optional) you can keep a tiny summary or actions in a plain column -->
        <b-table-column label="Actions">
          <template slot-scope="props">
            <button class="button is-small"
                    @click="toggleRowDetails(props.row)">
              {{ openedDetails.includes(props.row.id) ? 'Hide' : 'Show' }} portfolios
            </button>
          </template>
        </b-table-column>

        <!-- Details row: shown when expanded -->
        <template slot="detail" slot-scope="props">
          <div class="portfolio-list">
            <p class="title is-6">Portfolios in this Grouping:</p>

            <div v-if="props.row.model_portfolios.length > 0">
              <div v-for="portfolio in props.row.model_portfolios"
                  :key="portfolio.id"
                  class="portfolio-item-detailed">
                <div class="portfolio-header">
                  <span class="portfolio-name">{{ portfolio.nickname }}</span>
                  <button class="button is-small is-danger is-light remove-button"
                          @click="removePortfolioFromModelSet(props.row.id, portfolio.id)">
                    ✕
                  </button>
                </div>
                <div class="help is-size-7" style="margin-bottom: 0.5rem;">
                  Holdings as of {{ portfolio.holdings_as_of_date }}
                </div>

                <!-- Strategy Tags for this portfolio -->
                <div class="strategy-tags-section">
                  <div class="strategy-tags-label">
                    <span class="tag-label">Strategy Tags:</span>
                    <button 
                      class="button is-small is-light edit-strategies-btn" 
                      @click="openStrategyEditor(portfolio)"
                      title="Edit strategy tags"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                  </div>
                  <div class="strategy-tags">
                    <span 
                      v-for="strategy in portfolio.strategies" 
                      :key="strategy.id" 
                      class="tag is-info is-small"
                    >
                      {{ strategy.name }}
                    </span>
                    <span v-if="!portfolio.strategies || portfolio.strategies.length === 0" class="no-strategies">
                      No strategy tags assigned
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <TypeaheadPortfolioSearch
              placeholder="Add Portfolio to Model Set"
              :not_include_shareowners="true"
              :notAllowedIds="props.row.model_portfolios.map(p => p.id)"
              :callback="portfolio => addPortfolioToModelSet(props.row.id, portfolio)"
            />
          </div>
        </template>
      </b-table>
    </div>

    <!-- Strategy Editor Modal -->
    <div v-if="showStrategyEditor" class="modal is-active">
      <div class="modal-background" @click="closeStrategyEditor"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Edit Strategy Tags for {{ editingPortfolio?.nickname }}</p>
          <button class="delete" @click="closeStrategyEditor"></button>
        </header>
        <section class="modal-card-body">
          <div class="strategy-checkboxes">
            <label 
              v-for="strategy in availableStrategies" 
              :key="strategy.id"
              class="checkbox-item"
            >
              <input
                type="checkbox"
                :checked="isStrategySelected(strategy.id)"
                @change="toggleStrategy(strategy.id, $event.target.checked)"
                class="mr-2"
              />
              <span>{{ strategy.name }}</span>
            </label>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-success" @click="saveStrategyChanges">Save</button>
          <button class="button" @click="closeStrategyEditor">Cancel</button>
        </footer>
      </div>
    </div>

    <p v-if="saveMessage" class="notification is-success">{{ saveMessage }}</p>
  </div>
</template>

<script>
import TypeaheadPortfolioSearch from '../components/portfolios/TypeaheadPortfolioSearch.vue';
import { postModelSet, deleteModelSet } from '@frontend/api/modelsApi.js';
import Advisor from '../shared/Advisor.js';
import debounce from 'lodash/debounce';

export default {
  data() {
    return {
      modelSets: [],
      originalModelSets: [],
      host: null,
      hasUnsavedChanges: false,
      showCreateModelSetInput: false,
      newModelSetName: '',
      advisor: {},
      saveMessage: '',

      openedDetails: [],
      
      // Strategy management
      availableStrategies: [],
      showCreateStrategyInput: false,
      newStrategyName: '',
      
      // Strategy editor modal
      showStrategyEditor: false,
      editingPortfolio: null,
      selectedStrategies: [],
    }
  },
  components: {
    TypeaheadPortfolioSearch
  },
  computed: {
  },
  watch: {
    modelSets: {
      deep: true,
      handler: debounce(function() {
        // only send if there really are unsaved changes
        if (this.hasUnsavedChanges) {
          this.saveChanges()
        }
      }, 1500)  // wait 1.5s after the last change
    }
  },
  methods: {
    // Model Set Methods (keeping existing functionality)
    toggleRowDetails(row) {
      const id = row.id
      const i = this.openedDetails.indexOf(id)
      if (i === -1) this.openedDetails.push(id)
      else this.openedDetails.splice(i, 1)
    },
    saveNewModelSet: async function() {
      if (!this.newModelSetName.trim()) {
        alert('Model Set name cannot be empty.');
        return;
      }

      this.loading = true;
      console.log('DATA CSRF', this.$root.$data.csrfToken)

      try {
        const response = await postModelSet(
          this.newModelSetName.trim(),
          this.advisor.firm.id,
          this.$root.$data.csrfToken
        );

        if (response.ok) {
          this.modelSets.push({
            ...response.data,
            model_portfolios: []
          });
          this.originalModelSets = JSON.parse(JSON.stringify(this.modelSets));
          this.newModelSetName = '';
          this.showCreateModelSetInput = false;
        } else {
          console.error('Failed to create Model Set:', response.data);
          alert('Choose a unique name for your Model Set.');
        }
      } catch (error) {
        console.error('Error creating Model Set:', error);
        alert('An error occurred while creating the Model Set.');
      } finally {
        this.loading = false;
      }
    },
    cancelCreateModelSet: function() {
      this.showCreateModelSetInput = false;
      this.newModelSetName = '';
    },
    removeModelSet: async function(modelSetId) {
      if (confirm('Are you sure you want to delete this model set? This action cannot be undone.')) {
        try {
          const response = await deleteModelSet(modelSetId, this.$root.$data.csrfToken);

          if (response.ok) {
            this.modelSets = this.modelSets.filter(ms => ms.id !== modelSetId);
            this.originalModelSets = JSON.parse(JSON.stringify(this.modelSets));
          } else {
            console.error('Failed to delete Model Set:', response.data);
            alert('An error occurred while deleting the Model Set.');
          }
        } catch (error) {
          console.error('Error deleting Model Set:', error);
          alert('An error occurred while deleting the Model Set.');
        }
      }
    },
    markDirty() {
      this.hasUnsavedChanges = true
    },
    removePortfolioFromModelSet: function(modelSetId, portfolioId) {
      const modelSet = this.modelSets.find(ms => ms.id === modelSetId);
      if (modelSet && modelSet.model_portfolios) {
        console.log(`Removing portfolio ${portfolioId} from model set ${modelSetId}`);
        modelSet.model_portfolios = modelSet.model_portfolios.filter(p => p.id !== portfolioId);
        this.hasUnsavedChanges = true;
        this.markDirty();
      }
    },
    addPortfolioToModelSet: function(modelSetId, portfolio) {
      const modelSet = this.modelSets.find(ms => ms.id === modelSetId);

      if (portfolio && !modelSet.model_portfolios.some(p => p.id === portfolio.id)) {
        const newPortfolio = {
          id: portfolio.id,
          nickname: portfolio.nickname || portfolio.name || 'Unnamed Portfolio',
          status: portfolio.status || 'a',
          slug: portfolio.slug || '',
          created_date: portfolio.created_date || new Date().toISOString(),
          updated_at: portfolio.updated_at || new Date().toISOString(),
          fact_sheet_url: portfolio.fact_sheet_url || null,
          meta_external_id: portfolio.meta_external_id || null,
          external_id: portfolio.external_id || null,
          strategies: portfolio.strategies || [],
        };

        console.log('New portfolio object being added:', newPortfolio);
        modelSet.model_portfolios.push(newPortfolio);
      } else {
        console.log('Portfolio already in the model set');
      }

      this.hasUnsavedChanges = true;
      this.markDirty();
    },
    saveChanges: function() {
      this.modelSets.forEach(modelSet => {
        const originalModelSet = this.originalModelSets.find(oms => oms.id === modelSet.id);
        if (JSON.stringify(originalModelSet.model_portfolios) !== JSON.stringify(modelSet.model_portfolios)) {
          const payload = {
            model_ids: modelSet.model_portfolios.map(p => p.id)
          };

          console.log(`Saving changes for ModelSet ${modelSet.id}:`, payload);

          fetch(`/api/v1/model_sets/${modelSet.id}/`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.$root.$data.csrfToken,
            },
            body: JSON.stringify(payload)
          }).then(response => {
            if (response.ok) {
              this.originalModelSets = JSON.parse(JSON.stringify(this.modelSets));
              this.hasUnsavedChanges = false;
              this.saveMessage = 'Changes saved successfully!';
              setTimeout(() => {
                this.saveMessage = '';
              }, 3000);
            } else {
              console.error('Failed to save changes:', response);
            }
          }).catch(error => {
            console.error('Error saving changes:', error);
          });
        }
      });
    },
    discardChanges: function() {
      console.log('Discarding changes');
      this.modelSets = JSON.parse(JSON.stringify(this.originalModelSets));
      this.hasUnsavedChanges = false;
    },

    // Strategy Management Methods
    saveNewStrategy: async function() {
      if (!this.newStrategyName.trim()) {
        alert('Strategy tag name cannot be empty.');
        return;
      }

      this.loading = true;

      try {
        const response = await fetch('/api/v1/strategies/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({
            name: this.newStrategyName.trim()
          })
        });

        if (response.ok) {
          const data = await response.json();
          this.availableStrategies.push(data);
          this.newStrategyName = '';
          this.showCreateStrategyInput = false;
          this.saveMessage = 'Strategy tag created successfully!';
          setTimeout(() => {
            this.saveMessage = '';
          }, 3000);
        } else {
          console.error('Failed to create Strategy tag:', response);
          alert('Choose a unique name for your Strategy tag.');
        }
      } catch (error) {
        console.error('Error creating Strategy tag:', error);
        alert('An error occurred while creating the Strategy tag.');
      } finally {
        this.loading = false;
      }
    },
    cancelCreateStrategy: function() {
      this.showCreateStrategyInput = false;
      this.newStrategyName = '';
    },

    // Strategy Editor Methods
    openStrategyEditor: function(portfolio) {
      this.editingPortfolio = portfolio;
      console.error('portfolio strategies:', portfolio.strategies);
      this.selectedStrategies = (portfolio.strategies || []).map(s => s.id);
      this.showStrategyEditor = true;
    },
    closeStrategyEditor: function() {
      this.showStrategyEditor = false;
      this.editingPortfolio = null;
      this.selectedStrategies = [];
    },
    isStrategySelected: function(strategyId) {
      return this.selectedStrategies.includes(strategyId);
    },
    toggleStrategy: function(strategyId, isChecked) {
      if (isChecked) {
        if (!this.selectedStrategies.includes(strategyId)) {
          this.selectedStrategies.push(strategyId);
        }
      } else {
        this.selectedStrategies = this.selectedStrategies.filter(id => id !== strategyId);
      }
    },
    saveStrategyChanges: function() {
      if (this.editingPortfolio) {
        // Update the portfolio's strategies based on selected strategy IDs
        this.editingPortfolio.strategies = this.availableStrategies.filter(strategy => 
          this.selectedStrategies.includes(strategy.id)
        );
        
        // Save to backend
        const payload = {
          strategy_ids: this.selectedStrategies
        };

        fetch(`/api/v1/model_portfolios/${this.editingPortfolio.external_id}/strategies/`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify(payload)
        }).then(response => {
          if (response.ok) {
            this.saveMessage = 'Strategy assignments saved successfully!';
            setTimeout(() => {
              this.saveMessage = '';
            }, 3000);
          } else {
            console.error('Failed to save strategy assignments:', response);
            alert('An error occurred while saving strategy assignments.');
          }
        }).catch(error => {
          console.error('Error saving strategy assignments:', error);
          alert('An error occurred while saving strategy assignments.');
        });
        
        this.closeStrategyEditor();
      }
    },
  },
  created: function() {
    this.advisor = new Advisor();
    var self = this;
    
    this.$root.$data.csrfToken = document.querySelector('[name="csrfmiddlewaretoken"]').value;
    this.host = window.location.host;

    // Fetch model sets
    fetch('/api/v1/model_sets/')
      .then((response) => response.json())
      .then((data) => {
        console.log('Fetched model sets:', data);
        self.modelSets = (data.model_sets || []).map(modelSet => {
          console.log(`Model Set ${modelSet.id} portfolios:`, modelSet.model_portfolios);
          return {
            ...modelSet,
            model_portfolios: modelSet.model_portfolios || []
          };
        });
        self.originalModelSets = JSON.parse(JSON.stringify(self.modelSets));
        console.log('Model Sets after initialization:', self.modelSets);
      });

    // Fetch available strategies
    fetch('/api/v1/strategies/')
      .then((response) => response.json())
      .then((data) => {
        console.log('Fetched strategies:', data);
        self.availableStrategies = data.strategies || [];
        console.log('Available strategies:', self.availableStrategies);
      });
  },
}
</script>

<style scoped>
.icon.is-left {
  margin-right: 8px;
  color: #e74c3c;
}
.icon.is-left:hover {
  color: #c0392b;
}

.portfolio-list {
  padding-left: 0;
}

.portfolio-item-detailed {
  border: 1px solid #e1e1e1;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
  background-color: #fafafa;
}

.portfolio-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.portfolio-name {
  font-weight: 600;
  font-size: 1.1rem;
}

.remove-button {
  font-size: 0.875rem;
  padding: 0.25rem 0.5rem;
  border-radius: 50%;
  height: 1.5rem;
  width: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.strategy-tags-section {
  background-color: white;
  border-radius: 4px;
  padding: 0.75rem;
}

.strategy-tags-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.tag-label {
  font-weight: 500;
  color: #666;
  font-size: 0.9rem;
}

.edit-strategies-btn {
  padding: 0.25rem 0.5rem;
  height: auto;
}

.strategy-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.strategy-tags .tag {
  margin: 0;
}

.no-strategies {
  color: #999;
  font-style: italic;
  font-size: 0.9rem;
}

.strategy-checkboxes {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
  max-height: 300px;
  overflow-y: auto;
}

.checkbox-item {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
}

.checkbox-item:hover {
  background-color: #f5f5f5;
}

.modal-card {
  width: 90%;
  max-width: 600px;
}
.caret-btn { padding: 0 .25rem; margin-right: .25rem; }
.caret-btn .fa-chevron-right { transition: transform .15s ease; }
.caret-btn .fa-chevron-right.rotated { transform: rotate(90deg); }
</style>